import { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useDraftAutosave } from '../../hooks/useDraftAutosave';
import { DraftRestoreBanner, DraftSavedIndicator } from '../../components/DraftBanner';
import LayoutPreviewButton from '../../components/LayoutPreviewButton';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import {
  FileText, Plus, MagnifyingGlass, Eye, Trash, Download, PaperPlane, CircleNotch,
  CheckCircle, Paperclip, X, FilePdf, Warning
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_COLORS = {
  low: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  normal: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  high: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
  urgent: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
};

const STATUS_COLORS = {
  draft: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  final: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
};

function getToken() { return localStorage.getItem('erp_token'); }
function authHeaders() { return { Authorization: `Bearer ${getToken()}` }; }

export default function ReportsPage() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showView, setShowView] = useState(null);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [reportTypes, setReportTypes] = useState([]);
  const [customTypes, setCustomTypes] = useState([]);
  const [showAddType, setShowAddType] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');

  const initialFormState = {
    report_type: '', date: new Date().toISOString().split('T')[0],
    reference: '', subject: '', addressed_to: '', details: '', remarks: '',
    location: '', priority: 'normal',
    // PDF layout override (empty = use company default)
    layout_template: '',
    // Universal action tracker
    action_required: '', action_by: '', action_target_date: '',
    // Universal signature (any report type can toggle on)
    show_signature: false, signature_label: '', signatory_name: '', signatory_date: '', signature_data: '',
    // Completion
    project_reference: '', project_location: '', client_name: '',
    lpo_reference: '', work_details: '', additional_notes: '',
    confirmation_note: 'We hereby confirm the mentioned works are completed to the satisfaction of the client as per the specification and agreement scope.',
    client_signature_data: '', client_signature_name: '', client_signature_date: '',
    // Inspection
    inspection_type: '', inspected_by: '', inspection_date: '', inspection_time: '',
    items_inspected: '', findings: '', overall_status: '',
    // Daily / Weekly / Monthly
    period_from: '', period_to: '', period_label: '',
    weather: '', manpower_count: '', work_performed_today: '',
    materials_received_today: '', issues_delays: '', tomorrow_plan: '',
    cumulative_progress: '', period_highlights: '', completed_activities: '',
    pending_activities: '', risks_mitigation: '', next_period_plan: '',
    financial_invoiced: '', financial_received: '', financial_outstanding: '',
    executive_summary: '',
    // RFI
    rfi_raised_to: '', query_subject: '', query_reference_doc: '',
    query_detail: '', info_required_by: '', rfi_response: '',
    rfi_response_date: '', rfi_responded_by: '',
    // Incident
    incident_datetime: '', incident_type: '', severity: '', incident_location: '',
    people_involved: '', incident_description: '', immediate_action: '',
    root_cause: '', witnesses: '',
    // Progress Report
    work_scope: '', progress_this_week: '', planned_next_week: '',
    progress_this_week_items: [], planned_next_week_items: [],
    progress_percentage: '', client_notifications: '', contractor_notifications: '',
    awaited_items: [], signatures: [],
    linked_job_order_id: '', linked_agreement_id: '',
    critical_tasks: [],
    // Material Delivery Report
    delivery_items: [],
    delivered_to: '', delivered_by: '', vehicle_number: '',
    received_by_name: '', received_by_designation: '', received_by_date: '',
    received_by_signature_data: '',
    // Attachments — populated in edit mode from report.attachments
    attachments: [],
  };
  const [formData, setFormData] = useState(initialFormState);
  const [formLoading, setFormLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  // Files staged while creating a report — uploaded after the report is saved.
  const [pendingFiles, setPendingFiles] = useState([]);
  const { user } = useAuth();

  // Draft auto-save: scoped to report_type so each type has its own draft slot.
  // Disabled while editing an existing report.
  const reportDraft = useDraftAutosave({
    key: `report-${formData.report_type || 'new'}`,
    data: formData,
    enabled: showCreate && !editingId,
    userId: user?.id,
  });

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterType) params.append('report_type', filterType);
      if (filterStatus) params.append('status', filterStatus);
      const res = await fetch(`${API}/api/reports?${params}`, { headers: authHeaders() });
      if (res.ok) setReports(await res.json());
    } catch { toast.error('Failed to load reports'); }
    setLoading(false);
  }, [search, filterType, filterStatus]);

  const fetchTypes = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/reports/types`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setReportTypes(data.default_types || []);
        setCustomTypes(data.custom_types || []);
      }
    } catch {}
  }, []);

  useEffect(() => { fetchReports(); }, [fetchReports]);
  useEffect(() => { fetchTypes(); }, [fetchTypes]);

  const allTypes = [...reportTypes, ...customTypes];

  const resetForm = () => {
    setFormData(initialFormState);
    setEditingId(null);
  };

  // Fields whose backend type is int/float — empty strings must be coerced to null
  // (Pydantic rejects "" for numeric fields).
  const NUMERIC_FIELDS = [
    'manpower_count', 'cumulative_progress', 'progress_percentage',
    'financial_invoiced', 'financial_received', 'financial_outstanding',
  ];

  const normalizeForSubmit = (data) => {
    const out = { ...data };
    NUMERIC_FIELDS.forEach((k) => {
      if (out[k] === '' || out[k] === undefined) out[k] = null;
      else if (typeof out[k] === 'string' && out[k].trim() !== '') {
        const n = Number(out[k]);
        out[k] = Number.isFinite(n) ? n : null;
      }
    });
    // Trim other string fields that the user might leave as "" — Pydantic
    // tolerates this, but it keeps the DB tidy.
    return out;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.report_type) { toast.error('Please select a report type'); return; }
    if (!formData.subject) { toast.error('Please enter a subject'); return; }

    setFormLoading(true);
    try {
      const method = editingId ? 'PATCH' : 'POST';
      const url = editingId ? `${API}/api/reports/${editingId}` : `${API}/api/reports`;
      const payload = normalizeForSubmit(formData);
      const res = await fetch(url, {
        method, headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const saved = await res.json().catch(() => null);
        // Upload any files staged in the form (only on create — edit attaches inline)
        if (!editingId && pendingFiles.length > 0 && saved?.id) {
          let uploaded = 0;
          for (const f of pendingFiles) {
            try {
              const fd = new FormData();
              fd.append('file', f);
              const upRes = await fetch(`${API}/api/reports/${saved.id}/attachments`, {
                method: 'POST', headers: authHeaders(), body: fd,
              });
              if (upRes.ok) uploaded += 1;
            } catch { /* swallow — toast below summarises */ }
          }
          if (uploaded === pendingFiles.length) toast.success(`Uploaded ${uploaded} attachment${uploaded > 1 ? 's' : ''}`);
          else toast.warning(`Uploaded ${uploaded} of ${pendingFiles.length} attachments`);
        }
        toast.success(editingId ? 'Report updated' : 'Report created');
        if (!editingId) reportDraft.clear();
        setShowCreate(false);
        setPendingFiles([]);
        resetForm();
        fetchReports();
      } else {
        const err = await res.json();
        toast.error(err.detail || err.message || 'Failed to save report');
      }
    } catch { toast.error('Error saving report'); }
    setFormLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this report?')) return;
    try {
      await fetch(`${API}/api/reports/${id}`, { method: 'DELETE', headers: authHeaders() });
      toast.success('Report deleted');
      fetchReports();
    } catch { toast.error('Failed to delete'); }
  };

  const handleFinalize = async (id) => {
    try {
      const res = await fetch(`${API}/api/reports/${id}/finalize`, { method: 'POST', headers: authHeaders() });
      if (res.ok) { toast.success('Report finalized'); fetchReports(); if (showView?.id === id) { setShowView(await res.json()); } }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Error finalizing report'); }
  };

  const handleDownloadPdf = async (report) => {
    try {
      const res = await fetch(`${API}/api/reports/${report.id}/pdf`, { headers: authHeaders() });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${report.report_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download PDF'); }
  };

  const handleViewPdf = async (report) => {
    try {
      const res = await fetch(`${API}/api/reports/${report.id}/pdf?inline=true`, { headers: authHeaders() });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      // Open in new browser tab — the response uses Content-Disposition: inline
      // so the browser's native PDF viewer renders it.
      window.open(url, '_blank', 'noopener,noreferrer');
      // Release the object URL after a short delay to let the new tab load it
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error('Failed to open PDF'); }
  };

  const handleEdit = (report) => {
    const merged = { ...initialFormState };
    Object.keys(initialFormState).forEach((k) => {
      if (report[k] !== undefined && report[k] !== null) merged[k] = report[k];
    });
    // Preserve attachments list so the edit dialog can show + manage them.
    merged.attachments = report.attachments || [];
    setFormData(merged);
    setEditingId(report.id);
    setShowCreate(true);
  };

  const handleAddCustomType = async () => {
    if (!newTypeName.trim()) return;
    try {
      const res = await fetch(`${API}/api/reports/types`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newTypeName.trim() }),
      });
      if (res.ok) { toast.success('Report type added'); setNewTypeName(''); setShowAddType(false); fetchTypes(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Error adding type'); }
  };

  const handleUploadAttachment = async (reportId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${API}/api/reports/${reportId}/attachments`, {
        method: 'POST', headers: authHeaders(), body: fd,
      });
      if (res.ok) { toast.success('Attachment uploaded'); const updated = await fetch(`${API}/api/reports/${reportId}`, { headers: authHeaders() }); if (updated.ok) setShowView(await updated.json()); }
      else toast.error('Upload failed');
    } catch { toast.error('Upload error'); }
  };

  const handleDeleteAttachment = async (reportId, attachId) => {
    try {
      await fetch(`${API}/api/reports/${reportId}/attachments/${attachId}`, { method: 'DELETE', headers: authHeaders() });
      toast.success('Attachment removed');
      const updated = await fetch(`${API}/api/reports/${reportId}`, { headers: authHeaders() });
      if (updated.ok) setShowView(await updated.json());
    } catch { toast.error('Failed'); }
  };

  const stats = {
    total: reports.length,
    draft: reports.filter(r => r.status === 'draft').length,
    final: reports.filter(r => r.status === 'final').length,
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="reports-title">Reports</h1>
          <p className="text-sm text-muted-foreground">Prepare and manage custom business reports</p>
        </div>
        <Button onClick={() => { resetForm(); setShowCreate(true); }} data-testid="create-report-btn">
          <Plus size={16} className="mr-2" /> New Report
        </Button>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total Reports', value: stats.total, color: 'text-blue-600' },
          { label: 'Drafts', value: stats.draft, color: 'text-amber-600' },
          { label: 'Finalized', value: stats.final, color: 'text-emerald-600' },
        ].map((s, i) => (
          <div key={i} className="bg-card border rounded-xl p-4" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g,'-')}`}>
            <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">{s.label}</p>
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search reports..." className="pl-9" data-testid="search-reports" />
        </div>
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="filter-type">
          <option value="">All Types</option>
          {allTypes.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="filter-status">
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="final">Final</option>
        </select>
      </div>

      {/* Reports Table */}
      <div className="bg-card border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-bold text-muted-foreground">Report #</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-bold text-muted-foreground">Type</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-bold text-muted-foreground hidden md:table-cell">Subject</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-bold text-muted-foreground hidden lg:table-cell">Date</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-bold text-muted-foreground hidden lg:table-cell">Prepared By</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-bold text-muted-foreground">Priority</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-bold text-muted-foreground">Status</th>
                <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-bold text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground"><CircleNotch size={24} className="animate-spin mx-auto mb-2" />Loading...</td></tr>
              ) : reports.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-muted-foreground">
                  <FileText size={40} className="mx-auto mb-3 opacity-30" />
                  <p className="font-medium">No reports yet</p>
                  <p className="text-xs mt-1">Click "New Report" to create your first report</p>
                </td></tr>
              ) : reports.map(r => (
                <tr key={r.id} className="hover:bg-muted/30 transition-colors" data-testid={`report-row-${r.report_number}`}>
                  <td className="px-4 py-3 font-mono font-medium">{r.report_number}</td>
                  <td className="px-4 py-3 text-xs">{r.report_type}</td>
                  <td className="px-4 py-3 hidden md:table-cell truncate max-w-[200px]">{r.subject || '-'}</td>
                  <td className="px-4 py-3 hidden lg:table-cell">{r.date}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-xs">{r.prepared_by}</td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={PRIORITY_COLORS[r.priority] || ''}>{r.priority}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={STATUS_COLORS[r.status] || ''}>{r.status}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {r.status === 'draft' && (
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(r)} data-testid={`edit-${r.report_number}`}>
                          <FileText size={15} />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setShowView(r)} data-testid={`view-${r.report_number}`}>
                        <Eye size={15} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Attachments" onClick={() => setShowView(r)} data-testid={`attach-${r.report_number}`}>
                        <Paperclip size={15} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="View PDF" onClick={() => handleViewPdf(r)} data-testid={`view-pdf-${r.report_number}`}>
                        <FilePdf size={15} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Download PDF" onClick={() => handleDownloadPdf(r)} data-testid={`pdf-${r.report_number}`}>
                        <Download size={15} />
                      </Button>
                      {r.status === 'draft' && (
                        <>
                          <Button variant="ghost" size="sm" className="h-8 text-xs gap-1" onClick={() => handleFinalize(r.id)} data-testid={`finalize-${r.report_number}`}>
                            <CheckCircle size={14} /> Finalize
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700" onClick={() => handleDelete(r.id)} data-testid={`delete-${r.report_number}`}>
                            <Trash size={15} />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreate} onOpenChange={(o) => { if (!o) { setShowCreate(false); resetForm(); } }}>
        <DialogContent className="max-w-3xl max-h-[92vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>{editingId ? 'Edit Report' : 'New Report'}</DialogTitle>
            <p className="text-xs text-muted-foreground">Pick "Job Completion Report" (or any type with "Completion" in the name) to unlock project / client / signature / attachment fields.</p>
          </DialogHeader>
          <form onSubmit={handleCreate} className="overflow-y-auto flex-1 space-y-4 pr-1">
            {!editingId && (
              <DraftRestoreBanner
                visible={reportDraft.hasDraft}
                savedAt={reportDraft.draftSavedAt}
                label="report"
                onRestore={() => { const r = reportDraft.restore(); if (r) setFormData(r); }}
                onDiscard={reportDraft.discard}
              />
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Report Type <span className="text-red-500">*</span></Label>
                <div className="flex gap-2">
                  <select value={formData.report_type} onChange={e => setFormData(p => ({...p, report_type: e.target.value}))}
                    className="flex-1 h-9 rounded-md border border-input bg-background px-3 text-sm" required data-testid="report-type-select">
                    <option value="">Select type...</option>
                    {allTypes.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <Button type="button" variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={() => setShowAddType(true)} data-testid="add-type-btn">
                    <Plus size={14} />
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Date <span className="text-red-500">*</span></Label>
                <Input type="date" value={formData.date} onChange={e => setFormData(p => ({...p, date: e.target.value}))} required data-testid="report-date" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Subject <span className="text-red-500">*</span></Label>
              <Input value={formData.subject} onChange={e => setFormData(p => ({...p, subject: e.target.value}))}
                placeholder="Brief description of this report" required data-testid="report-subject" />
            </div>

            {/* ---- Type-specific panels — swap based on report_type ---- */}
            {(() => {
              const t = (formData.report_type || '').toLowerCase();
              const isInspection = t.includes('inspection');
              const isDaily = t.includes('daily');
              const isWeekly = t.includes('weekly');
              const isMonthly = t.includes('monthly');
              const isRfi = t.includes('rfi') || t.includes('request for info');
              const isIncident = t.includes('incident');
              const isProgress = t.includes('progress');
              const isDelivery = t.includes('delivery');
              const isPeriod = isWeekly || isMonthly;
              const setF = (k) => (e) => {
                const v = e?.target ? e.target.value : e;
                setFormData(p => ({ ...p, [k]: v }));
                if (e?.target && e.target.tagName === 'TEXTAREA') {
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 320) + 'px';
                }
              };
              const PanelWrap = ({ title, color, children }) => (
                <div className={`rounded-lg border bg-${color}-50/50 dark:bg-${color}-950/20 p-4 space-y-3`}
                  style={{ borderColor: 'var(--' + color + '-300, #fde68a)' }}>
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <CheckCircle size={16} /> {title}
                  </div>
                  {children}
                </div>
              );
              return (
                <>
                  {(isInspection || isDaily || isPeriod || isRfi || isIncident || isProgress || isDelivery) && (
                    <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50/50 dark:bg-amber-950/20 p-4 space-y-3">
                      <div className="flex items-center gap-2 text-sm font-semibold text-amber-900 dark:text-amber-200">
                        <CheckCircle size={16} /> {
                          isInspection ? 'Inspection Report Details' :
                          isDaily ? 'Daily Report Details' :
                          isWeekly ? 'Weekly Report Details' :
                          isMonthly ? 'Monthly Report Details' :
                          isRfi ? 'RFI Details' :
                          isProgress ? 'Progress Report Details' :
                          isDelivery ? 'Material Delivery Report Details' :
                          'Incident Report Details'
                        }
                      </div>
                      {/* Project ref/loc — common to all field reports */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1.5"><Label className="text-xs">Project Reference</Label>
                          <Input value={formData.project_reference} onChange={setF('project_reference')} placeholder="PRJ-2026-001" data-testid="rt-project-ref" /></div>
                        <div className="space-y-1.5"><Label className="text-xs">Project Location</Label>
                          <Input value={formData.project_location} onChange={setF('project_location')} placeholder="Site B, Tower 2" data-testid="rt-project-loc" /></div>
                      </div>

                      {/* ===== Inspection ===== */}
                      {isInspection && (
                        <>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-1.5"><Label className="text-xs">Inspection Type</Label>
                              <Select value={formData.inspection_type} onValueChange={setF('inspection_type')}>
                                <SelectTrigger data-testid="rt-insp-type"><SelectValue placeholder="Choose…" /></SelectTrigger>
                                <SelectContent>
                                  {['Pre-handover','Quality','Safety','Final','Routine','Snag'].map(o => <SelectItem key={o} value={o.toLowerCase()}>{o}</SelectItem>)}
                                </SelectContent></Select></div>
                            <div className="space-y-1.5"><Label className="text-xs">Inspected By</Label>
                              <Input value={formData.inspected_by} onChange={setF('inspected_by')} placeholder="John Smith, QC Engineer" /></div>
                            <div className="space-y-1.5"><Label className="text-xs">Inspection Date</Label>
                              <Input type="date" value={formData.inspection_date} onChange={setF('inspection_date')} /></div>
                            <div className="space-y-1.5"><Label className="text-xs">Inspection Time</Label>
                              <Input type="time" value={formData.inspection_time} onChange={setF('inspection_time')} /></div>
                          </div>
                          <div className="space-y-1.5"><Label className="text-xs">Items Inspected</Label>
                            <Textarea value={formData.items_inspected} onChange={setF('items_inspected')} rows={3} className="resize-none overflow-hidden min-h-[80px]" placeholder="One item per line" data-testid="rt-items" /></div>
                          <div className="space-y-1.5"><Label className="text-xs">Findings / Observations <span className="text-red-500">*</span></Label>
                            <Textarea value={formData.findings} onChange={setF('findings')} rows={4} className="resize-none overflow-hidden min-h-[100px]" placeholder="Describe what was found" data-testid="rt-findings" /></div>
                          <div className="space-y-1.5"><Label className="text-xs">Overall Status</Label>
                            <Select value={formData.overall_status} onValueChange={setF('overall_status')}>
                              <SelectTrigger data-testid="rt-overall-status"><SelectValue placeholder="Choose…" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="passed">Passed</SelectItem>
                                <SelectItem value="passed_observations">Passed with Observations</SelectItem>
                                <SelectItem value="failed">Failed</SelectItem>
                                <SelectItem value="re_inspection">Re-Inspection Required</SelectItem>
                              </SelectContent></Select></div>
                        </>
                      )}

                      {/* ===== Daily ===== */}
                      {isDaily && (
                        <>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-1.5"><Label className="text-xs">Weather / Site Conditions</Label>
                              <Select value={formData.weather} onValueChange={setF('weather')}>
                                <SelectTrigger data-testid="rt-weather"><SelectValue placeholder="Choose…" /></SelectTrigger>
                                <SelectContent>
                                  {['Sunny','Cloudy','Rainy','Windy','Hot','Dusty','N/A'].map(o => <SelectItem key={o} value={o.toLowerCase()}>{o}</SelectItem>)}
                                </SelectContent></Select></div>
                            <div className="space-y-1.5"><Label className="text-xs">Manpower on Site</Label>
                              <Input type="number" min="0" value={formData.manpower_count} onChange={setF('manpower_count')} placeholder="12" data-testid="rt-manpower" /></div>
                          </div>
                          <div className="space-y-1.5"><Label className="text-xs">Work Performed Today</Label>
                            <Textarea value={formData.work_performed_today} onChange={setF('work_performed_today')} rows={4} className="resize-none overflow-hidden min-h-[100px]" data-testid="rt-work-today" /></div>
                          <div className="space-y-1.5"><Label className="text-xs">Materials Received Today</Label>
                            <Textarea value={formData.materials_received_today} onChange={setF('materials_received_today')} rows={3} className="resize-none overflow-hidden min-h-[80px]" /></div>
                          <div className="space-y-1.5"><Label className="text-xs">Issues / Delays</Label>
                            <Textarea value={formData.issues_delays} onChange={setF('issues_delays')} rows={3} className="resize-none overflow-hidden min-h-[80px]" data-testid="rt-issues" /></div>
                          <div className="space-y-1.5"><Label className="text-xs">Tomorrow&apos;s Planned Work</Label>
                            <Textarea value={formData.tomorrow_plan} onChange={setF('tomorrow_plan')} rows={3} className="resize-none overflow-hidden min-h-[80px]" data-testid="rt-tomorrow" /></div>
                        </>
                      )}

                      {/* ===== Weekly / Monthly ===== */}
                      {isPeriod && (
                        <>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div className="space-y-1.5"><Label className="text-xs">Period From</Label>
                              <Input type="date" value={formData.period_from} onChange={setF('period_from')} data-testid="rt-period-from" /></div>
                            <div className="space-y-1.5"><Label className="text-xs">Period To</Label>
                              <Input type="date" value={formData.period_to} onChange={setF('period_to')} data-testid="rt-period-to" /></div>
                            <div className="space-y-1.5"><Label className="text-xs">{isMonthly ? 'Month Label' : 'Week Label'}</Label>
                              <Input value={formData.period_label} onChange={setF('period_label')} placeholder={isMonthly ? 'March 2026' : 'Week 12, 2026'} /></div>
                          </div>
                          <div className="space-y-1.5"><Label className="text-xs">Cumulative Progress (%)</Label>
                            <Input type="number" min="0" max="100" step="0.5" value={formData.cumulative_progress} onChange={setF('cumulative_progress')} placeholder="42.5" data-testid="rt-progress" /></div>
                          {isMonthly && (
                            <>
                              <div className="space-y-1.5"><Label className="text-xs">Executive Summary</Label>
                                <Textarea value={formData.executive_summary} onChange={setF('executive_summary')} rows={3} className="resize-none overflow-hidden min-h-[80px]" /></div>
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <div className="space-y-1.5"><Label className="text-xs">Invoiced This Period</Label>
                                  <Input type="number" step="0.01" value={formData.financial_invoiced} onChange={setF('financial_invoiced')} /></div>
                                <div className="space-y-1.5"><Label className="text-xs">Received This Period</Label>
                                  <Input type="number" step="0.01" value={formData.financial_received} onChange={setF('financial_received')} /></div>
                                <div className="space-y-1.5"><Label className="text-xs">Outstanding</Label>
                                  <Input type="number" step="0.01" value={formData.financial_outstanding} onChange={setF('financial_outstanding')} /></div>
                              </div>
                            </>
                          )}
                          <div className="space-y-1.5"><Label className="text-xs">Highlights</Label>
                            <Textarea value={formData.period_highlights} onChange={setF('period_highlights')} rows={3} className="resize-none overflow-hidden min-h-[80px]" /></div>
                          <div className="space-y-1.5"><Label className="text-xs">Completed Activities</Label>
                            <Textarea value={formData.completed_activities} onChange={setF('completed_activities')} rows={4} className="resize-none overflow-hidden min-h-[100px]" /></div>
                          <div className="space-y-1.5"><Label className="text-xs">Pending / In-Progress</Label>
                            <Textarea value={formData.pending_activities} onChange={setF('pending_activities')} rows={3} className="resize-none overflow-hidden min-h-[80px]" /></div>
                          <div className="space-y-1.5"><Label className="text-xs">Risks &amp; Mitigation</Label>
                            <Textarea value={formData.risks_mitigation} onChange={setF('risks_mitigation')} rows={3} className="resize-none overflow-hidden min-h-[80px]" /></div>
                          <div className="space-y-1.5"><Label className="text-xs">{isMonthly ? 'Outlook for Next Month' : "Next Week's Plan"}</Label>
                            <Textarea value={formData.next_period_plan} onChange={setF('next_period_plan')} rows={3} className="resize-none overflow-hidden min-h-[80px]" /></div>
                        </>
                      )}

                      {/* ===== RFI ===== */}
                      {isRfi && (
                        <>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-1.5"><Label className="text-xs">Raised To</Label>
                              <Input value={formData.rfi_raised_to} onChange={setF('rfi_raised_to')} placeholder="Architect / Consultant" data-testid="rt-rfi-to" /></div>
                            <div className="space-y-1.5"><Label className="text-xs">Query Subject</Label>
                              <Input value={formData.query_subject} onChange={setF('query_subject')} placeholder="Wall finish clarification" /></div>
                            <div className="space-y-1.5"><Label className="text-xs">Reference Drawing / Spec</Label>
                              <Input value={formData.query_reference_doc} onChange={setF('query_reference_doc')} placeholder="DWG-A-100" /></div>
                            <div className="space-y-1.5"><Label className="text-xs">Information Required By</Label>
                              <Input type="date" value={formData.info_required_by} onChange={setF('info_required_by')} /></div>
                          </div>
                          <div className="space-y-1.5"><Label className="text-xs">Detailed Question <span className="text-red-500">*</span></Label>
                            <Textarea value={formData.query_detail} onChange={setF('query_detail')} rows={4} className="resize-none overflow-hidden min-h-[100px]" data-testid="rt-query-detail" /></div>
                          <div className="pt-2 border-t border-amber-200 dark:border-amber-900/40 space-y-1.5">
                            <Label className="text-xs text-amber-900 dark:text-amber-300">Response (fill once received)</Label>
                            <Textarea value={formData.rfi_response} onChange={setF('rfi_response')} rows={3} className="resize-none overflow-hidden min-h-[80px]" /></div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-1.5"><Label className="text-xs">Response Date</Label>
                              <Input type="date" value={formData.rfi_response_date} onChange={setF('rfi_response_date')} /></div>
                            <div className="space-y-1.5"><Label className="text-xs">Responded By</Label>
                              <Input value={formData.rfi_responded_by} onChange={setF('rfi_responded_by')} /></div>
                          </div>
                        </>
                      )}

                      {/* ===== Incident ===== */}
                      {isIncident && (
                        <>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-1.5"><Label className="text-xs">Incident Date &amp; Time <span className="text-red-500">*</span></Label>
                              <Input type="datetime-local" value={formData.incident_datetime} onChange={setF('incident_datetime')} data-testid="rt-incident-dt" /></div>
                            <div className="space-y-1.5"><Label className="text-xs">Incident Type</Label>
                              <Select value={formData.incident_type} onValueChange={setF('incident_type')}>
                                <SelectTrigger><SelectValue placeholder="Choose…" /></SelectTrigger>
                                <SelectContent>
                                  {['Injury','Near miss','Property damage','Environmental','Security','Fire','Other'].map(o => <SelectItem key={o} value={o.toLowerCase().replace(' ','_')}>{o}</SelectItem>)}
                                </SelectContent></Select></div>
                            <div className="space-y-1.5"><Label className="text-xs">Severity</Label>
                              <Select value={formData.severity} onValueChange={setF('severity')}>
                                <SelectTrigger data-testid="rt-severity"><SelectValue placeholder="Choose…" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="minor">Minor</SelectItem>
                                  <SelectItem value="moderate">Moderate</SelectItem>
                                  <SelectItem value="major">Major</SelectItem>
                                  <SelectItem value="critical">Critical</SelectItem>
                                </SelectContent></Select></div>
                            <div className="space-y-1.5"><Label className="text-xs">Location of Incident</Label>
                              <Input value={formData.incident_location} onChange={setF('incident_location')} placeholder="Level 4 East side" /></div>
                          </div>
                          <div className="space-y-1.5"><Label className="text-xs">People Involved</Label>
                            <Textarea value={formData.people_involved} onChange={setF('people_involved')} rows={2} className="resize-none overflow-hidden min-h-[60px]" placeholder="Names + roles, one per line" /></div>
                          <div className="space-y-1.5"><Label className="text-xs">Description of Incident <span className="text-red-500">*</span></Label>
                            <Textarea value={formData.incident_description} onChange={setF('incident_description')} rows={4} className="resize-none overflow-hidden min-h-[100px]" data-testid="rt-incident-desc" /></div>
                          <div className="space-y-1.5"><Label className="text-xs">Immediate Action Taken</Label>
                            <Textarea value={formData.immediate_action} onChange={setF('immediate_action')} rows={3} className="resize-none overflow-hidden min-h-[80px]" /></div>
                          <div className="space-y-1.5"><Label className="text-xs">Root Cause Analysis</Label>
                            <Textarea value={formData.root_cause} onChange={setF('root_cause')} rows={3} className="resize-none overflow-hidden min-h-[80px]" /></div>
                          <div className="space-y-1.5"><Label className="text-xs">Witnesses</Label>
                            <Textarea value={formData.witnesses} onChange={setF('witnesses')} rows={2} className="resize-none overflow-hidden min-h-[60px]" /></div>
                        </>
                      )}

                      {/* ===== Material Delivery Report ===== */}
                      {isDelivery && (
                        <>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-1.5"><Label className="text-xs">LPO / Order Reference <span className="text-red-500">*</span></Label>
                              <Input value={formData.lpo_reference || ''} onChange={setF('lpo_reference')} placeholder="LPO #12345" data-testid="rt-delivery-lpo" /></div>
                            <div className="space-y-1.5"><Label className="text-xs">Delivered To</Label>
                              <Input value={formData.delivered_to || ''} onChange={setF('delivered_to')} placeholder="Client name / Site custodian" data-testid="rt-delivery-to" /></div>
                            <div className="space-y-1.5"><Label className="text-xs">Delivered By (Driver / Team)</Label>
                              <Input value={formData.delivered_by || ''} onChange={setF('delivered_by')} placeholder="Mr. Ahmed / Supplier team" data-testid="rt-delivery-by" /></div>
                            <div className="space-y-1.5"><Label className="text-xs">Vehicle / Trip #</Label>
                              <Input value={formData.vehicle_number || ''} onChange={setF('vehicle_number')} placeholder="Truck plate / Trip ID" data-testid="rt-delivery-vehicle" /></div>
                          </div>

                          {/* Dynamic delivery items table */}
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label className="text-xs font-semibold">Delivered Items <span className="text-red-500">*</span></Label>
                              <Button type="button" size="sm" variant="outline" onClick={() => {
                                const rows = Array.isArray(formData.delivery_items) ? [...formData.delivery_items] : [];
                                rows.push({ description: '', quantity: '', unit: '', remarks: '' });
                                setFormData(p => ({ ...p, delivery_items: rows }));
                              }} data-testid="rt-add-delivery-item">+ Add Item</Button>
                            </div>
                            {(formData.delivery_items || []).length === 0 && (
                              <div className="text-xs text-muted-foreground italic">No items added yet. Click "+ Add Item" to list what was delivered.</div>
                            )}
                            {(formData.delivery_items || []).map((row, idx) => (
                              <div key={idx} className="grid grid-cols-12 gap-1 items-start bg-background rounded border border-border/60 p-1.5">
                                <span className="col-span-1 text-xs text-muted-foreground text-center mt-1.5">{idx + 1}.</span>
                                <Input className="col-span-5 h-8 text-xs" placeholder="Item description" value={row.description || ''} onChange={(e) => {
                                  const arr = [...formData.delivery_items]; arr[idx] = { ...arr[idx], description: e.target.value }; setFormData(p => ({ ...p, delivery_items: arr }));
                                }} data-testid={`rt-delivery-item-desc-${idx}`} />
                                <Input className="col-span-2 h-8 text-xs" placeholder="Qty" value={row.quantity || ''} onChange={(e) => {
                                  const arr = [...formData.delivery_items]; arr[idx] = { ...arr[idx], quantity: e.target.value }; setFormData(p => ({ ...p, delivery_items: arr }));
                                }} data-testid={`rt-delivery-item-qty-${idx}`} />
                                <Input className="col-span-1 h-8 text-xs" placeholder="Unit" value={row.unit || ''} onChange={(e) => {
                                  const arr = [...formData.delivery_items]; arr[idx] = { ...arr[idx], unit: e.target.value }; setFormData(p => ({ ...p, delivery_items: arr }));
                                }} data-testid={`rt-delivery-item-unit-${idx}`} />
                                <Input className="col-span-2 h-8 text-xs" placeholder="Remarks" value={row.remarks || ''} onChange={(e) => {
                                  const arr = [...formData.delivery_items]; arr[idx] = { ...arr[idx], remarks: e.target.value }; setFormData(p => ({ ...p, delivery_items: arr }));
                                }} />
                                <Button type="button" size="icon" variant="ghost" className="col-span-1 h-7 w-7" onClick={() => {
                                  setFormData(p => ({ ...p, delivery_items: p.delivery_items.filter((_, i) => i !== idx) }));
                                }}>✕</Button>
                              </div>
                            ))}
                          </div>

                          {/* Received By */}
                          <div className="pt-2 border-t border-amber-200 dark:border-amber-900/40 space-y-3">
                            <Label className="text-xs font-semibold">Received By</Label>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                              <div className="space-y-1.5"><Label className="text-xs">Recipient Name</Label>
                                <Input value={formData.received_by_name || ''} onChange={setF('received_by_name')} placeholder="Mr. Ahmed Khan" data-testid="rt-received-name" /></div>
                              <div className="space-y-1.5"><Label className="text-xs">Designation</Label>
                                <Input value={formData.received_by_designation || ''} onChange={setF('received_by_designation')} placeholder="Store Keeper / Foreman" data-testid="rt-received-designation" /></div>
                              <div className="space-y-1.5"><Label className="text-xs">Received On</Label>
                                <Input type="date" value={formData.received_by_date || ''} onChange={setF('received_by_date')} data-testid="rt-received-date" /></div>
                            </div>
                            <SignaturePadField
                              value={formData.received_by_signature_data}
                              onChange={(dataUrl) => setFormData(p => ({ ...p, received_by_signature_data: dataUrl }))}
                            />
                          </div>
                        </>
                      )}

                      {/* ===== Progress Report ===== */}
                      {isProgress && (
                        <>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-1.5"><Label className="text-xs">Overall Completion % <span className="text-muted-foreground">(auto-computed if tasks below)</span></Label>
                              <Input type="number" min={0} max={100} step={0.1} value={formData.progress_percentage ?? ''} onChange={setF('progress_percentage')} placeholder="e.g. 65" data-testid="rt-progress-pct" /></div>
                            <div className="space-y-1.5"><Label className="text-xs">Client Name</Label>
                              <Input value={formData.client_name || ''} onChange={setF('client_name')} placeholder="Client / Owner" /></div>
                          </div>

                          {/* Critical Tasks — project dashboard driver */}
                          <CriticalTasksPanel
                            value={formData.critical_tasks || []}
                            onChange={(next) => setFormData(p => ({ ...p, critical_tasks: next }))}
                            reportId={editingId}
                          />

                          <div className="space-y-1.5"><Label className="text-xs">Scope of Works</Label>
                            <Textarea value={formData.work_scope || ''} onChange={setF('work_scope')} rows={3} className="resize-none overflow-hidden min-h-[80px]" placeholder="Brief scope covering the works in this progress period" data-testid="rt-work-scope" /></div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                              <div className="flex items-center justify-between">
                                <Label className="text-xs">Progress This Week</Label>
                                <Button type="button" size="sm" variant="outline" onClick={() => {
                                  const rows = Array.isArray(formData.progress_this_week_items) ? [...formData.progress_this_week_items] : [];
                                  rows.push({ work_details: '', status: '' });
                                  setFormData(p => ({ ...p, progress_this_week_items: rows }));
                                }} data-testid="rt-add-progress-item">+ Add Line</Button>
                              </div>
                              <div className="space-y-1.5">
                                {(formData.progress_this_week_items || []).map((row, idx) => (
                                  <div key={idx} className="grid grid-cols-12 gap-1 items-start bg-background rounded border border-border/60 p-1.5">
                                    <span className="col-span-1 text-xs text-muted-foreground text-center mt-1.5">{idx + 1}.</span>
                                    <Input className="col-span-7 h-8 text-xs" placeholder="Work details" value={row.work_details || ''} onChange={(e) => {
                                      const arr = [...formData.progress_this_week_items]; arr[idx] = { ...arr[idx], work_details: e.target.value }; setFormData(p => ({ ...p, progress_this_week_items: arr }));
                                    }} />
                                    <Input className="col-span-3 h-8 text-xs" placeholder="Status / %" value={row.status || ''} onChange={(e) => {
                                      const arr = [...formData.progress_this_week_items]; arr[idx] = { ...arr[idx], status: e.target.value }; setFormData(p => ({ ...p, progress_this_week_items: arr }));
                                    }} />
                                    <Button type="button" size="icon" variant="ghost" className="col-span-1 h-7 w-7" onClick={() => {
                                      setFormData(p => ({ ...p, progress_this_week_items: p.progress_this_week_items.filter((_, i) => i !== idx) }));
                                    }}>✕</Button>
                                  </div>
                                ))}
                                {(!formData.progress_this_week_items || formData.progress_this_week_items.length === 0) && (
                                  <Textarea value={formData.progress_this_week || ''} onChange={setF('progress_this_week')} rows={3} className="resize-none overflow-hidden min-h-[80px] text-xs" placeholder="Add lines via +Add Line, or type a paragraph here" />
                                )}
                              </div>
                            </div>
                            <div className="space-y-1.5">
                              <div className="flex items-center justify-between">
                                <Label className="text-xs">Planned for Next Week</Label>
                                <Button type="button" size="sm" variant="outline" onClick={() => {
                                  const rows = Array.isArray(formData.planned_next_week_items) ? [...formData.planned_next_week_items] : [];
                                  rows.push({ work_details: '', status: '' });
                                  setFormData(p => ({ ...p, planned_next_week_items: rows }));
                                }} data-testid="rt-add-planned-item">+ Add Line</Button>
                              </div>
                              <div className="space-y-1.5">
                                {(formData.planned_next_week_items || []).map((row, idx) => (
                                  <div key={idx} className="grid grid-cols-12 gap-1 items-start bg-background rounded border border-border/60 p-1.5">
                                    <span className="col-span-1 text-xs text-muted-foreground text-center mt-1.5">{idx + 1}.</span>
                                    <Input className="col-span-7 h-8 text-xs" placeholder="Work details" value={row.work_details || ''} onChange={(e) => {
                                      const arr = [...formData.planned_next_week_items]; arr[idx] = { ...arr[idx], work_details: e.target.value }; setFormData(p => ({ ...p, planned_next_week_items: arr }));
                                    }} />
                                    <Input className="col-span-3 h-8 text-xs" placeholder="Target / date" value={row.status || ''} onChange={(e) => {
                                      const arr = [...formData.planned_next_week_items]; arr[idx] = { ...arr[idx], status: e.target.value }; setFormData(p => ({ ...p, planned_next_week_items: arr }));
                                    }} />
                                    <Button type="button" size="icon" variant="ghost" className="col-span-1 h-7 w-7" onClick={() => {
                                      setFormData(p => ({ ...p, planned_next_week_items: p.planned_next_week_items.filter((_, i) => i !== idx) }));
                                    }}>✕</Button>
                                  </div>
                                ))}
                                {(!formData.planned_next_week_items || formData.planned_next_week_items.length === 0) && (
                                  <Textarea value={formData.planned_next_week || ''} onChange={setF('planned_next_week')} rows={3} className="resize-none overflow-hidden min-h-[80px] text-xs" placeholder="Add lines via +Add Line, or type a paragraph here" />
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-1.5"><Label className="text-xs">Notifications to Client</Label>
                              <Textarea value={formData.client_notifications || ''} onChange={setF('client_notifications')} rows={2} className="resize-none overflow-hidden min-h-[60px]" placeholder="Anything the client should know / decide" /></div>
                            <div className="space-y-1.5"><Label className="text-xs">Notifications to Contractor</Label>
                              <Textarea value={formData.contractor_notifications || ''} onChange={setF('contractor_notifications')} rows={2} className="resize-none overflow-hidden min-h-[60px]" placeholder="Anything the contractor / subcontractor should action" /></div>
                          </div>

                          {/* Awaited items — dynamic list */}
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label className="text-xs font-semibold">Information / Approvals Awaited</Label>
                              <Button type="button" size="sm" variant="outline" onClick={() => {
                                const rows = Array.isArray(formData.awaited_items) ? [...formData.awaited_items] : [];
                                rows.push({ item: '', requested_from: '', requested_on: new Date().toISOString().slice(0,10), status: 'Pending' });
                                setFormData(p => ({ ...p, awaited_items: rows }));
                              }} data-testid="rt-add-awaited">+ Add Row</Button>
                            </div>
                            {(formData.awaited_items || []).length === 0 && (
                              <div className="text-xs text-muted-foreground italic">No items awaiting response. Click "+ Add Row" if you have pending queries or approvals.</div>
                            )}
                            {(formData.awaited_items || []).map((row, idx) => (
                              <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-background rounded border border-border/60 p-2">
                                <Input className="col-span-4 h-8" placeholder="Item / Information needed" value={row.item || ''} onChange={(e) => {
                                  const arr = [...formData.awaited_items]; arr[idx] = { ...arr[idx], item: e.target.value }; setFormData(p => ({ ...p, awaited_items: arr }));
                                }} />
                                <Input className="col-span-3 h-8" placeholder="Requested from" value={row.requested_from || ''} onChange={(e) => {
                                  const arr = [...formData.awaited_items]; arr[idx] = { ...arr[idx], requested_from: e.target.value }; setFormData(p => ({ ...p, awaited_items: arr }));
                                }} />
                                <Input className="col-span-2 h-8" type="date" value={row.requested_on || ''} onChange={(e) => {
                                  const arr = [...formData.awaited_items]; arr[idx] = { ...arr[idx], requested_on: e.target.value }; setFormData(p => ({ ...p, awaited_items: arr }));
                                }} />
                                <Input className="col-span-2 h-8" placeholder="Status" value={row.status || ''} onChange={(e) => {
                                  const arr = [...formData.awaited_items]; arr[idx] = { ...arr[idx], status: e.target.value }; setFormData(p => ({ ...p, awaited_items: arr }));
                                }} />
                                <Button type="button" size="icon" variant="ghost" className="col-span-1 h-7 w-7" onClick={() => {
                                  const arr = formData.awaited_items.filter((_, i) => i !== idx);
                                  setFormData(p => ({ ...p, awaited_items: arr }));
                                }}>✕</Button>
                              </div>
                            ))}
                          </div>

                          {/* Three signature blocks */}
                          <div className="space-y-2">
                            <Label className="text-xs font-semibold">Signatures (Prepared / Client / Contractor)</Label>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                              {['Prepared By', 'Client', 'Contractor'].map((defLabel, i) => {
                                const s = (formData.signatures && formData.signatures[i]) || { label: defLabel, name: '', contact: '' };
                                const update = (k, v) => {
                                  const arr = Array.isArray(formData.signatures) ? [...formData.signatures] : [];
                                  while (arr.length < 3) arr.push({ label: ['Prepared By','Client','Contractor'][arr.length], name: '', contact: '' });
                                  arr[i] = { ...arr[i], [k]: v };
                                  setFormData(p => ({ ...p, signatures: arr }));
                                };
                                return (
                                  <div key={i} className="rounded border border-border/60 bg-background p-2 space-y-1">
                                    <Input className="h-8" placeholder="Role / Label" value={s.label} onChange={(e) => update('label', e.target.value)} />
                                    <Input className="h-8" placeholder="Name" value={s.name} onChange={(e) => update('name', e.target.value)} />
                                    <Input className="h-8" placeholder="Contact (email / phone)" value={s.contact} onChange={(e) => update('contact', e.target.value)} />
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </>
              );
            })()}

            {/* ---- Universal Action Required (any type) ---- */}
            {!(formData.report_type || '').toLowerCase().includes('completion') && formData.report_type && (
              <div className="rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50/40 dark:bg-red-950/20 p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-red-900 dark:text-red-200">
                  <CheckCircle size={16} /> Action Required (Optional)
                </div>
                <div className="space-y-1.5">
                  <Textarea value={formData.action_required} onChange={(e) => {
                    setFormData(p => ({...p, action_required: e.target.value}));
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
                  }} rows={2} placeholder="What needs to happen next? Leave blank if no action required." className="resize-none overflow-hidden min-h-[60px]" data-testid="rt-action-required" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label className="text-xs">Action By (Person / Team)</Label>
                    <Input value={formData.action_by} onChange={(e) => setFormData(p => ({...p, action_by: e.target.value}))} placeholder="Foreman / QC team / Mr. X" data-testid="rt-action-by" /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Target Closure Date</Label>
                    <Input type="date" value={formData.action_target_date} onChange={(e) => setFormData(p => ({...p, action_target_date: e.target.value}))} data-testid="rt-action-target" /></div>
                </div>
              </div>
            )}

            {/* ---- Universal Signature (any non-completion report type) ---- */}
            {formData.report_type && !(formData.report_type || '').toLowerCase().includes('completion') && (
              <div className="rounded-lg border border-indigo-200 dark:border-indigo-900/40 bg-indigo-50/40 dark:bg-indigo-950/20 p-4 space-y-3">
                <label className="flex items-center gap-2 text-sm font-semibold text-indigo-900 dark:text-indigo-200 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!formData.show_signature}
                    onChange={e => setFormData(p => ({ ...p, show_signature: e.target.checked }))}
                    data-testid="rt-show-signature-toggle"
                  />
                  Include signature on the printed report
                </label>
                {formData.show_signature && (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs">Signature Label</Label>
                        <Input value={formData.signature_label}
                          onChange={e => setFormData(p => ({ ...p, signature_label: e.target.value }))}
                          placeholder="Engineer / Client / Witness" data-testid="rt-signature-label" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Signatory Name</Label>
                        <Input value={formData.signatory_name}
                          onChange={e => setFormData(p => ({ ...p, signatory_name: e.target.value }))}
                          placeholder="Mr. Ahmed Khan" data-testid="rt-signatory-name" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Signed On</Label>
                        <Input type="date" value={formData.signatory_date}
                          onChange={e => setFormData(p => ({ ...p, signatory_date: e.target.value }))}
                          data-testid="rt-signatory-date" />
                      </div>
                    </div>
                    <SignaturePadField
                      value={formData.signature_data}
                      onChange={(dataUrl) => setFormData(p => ({ ...p, signature_data: dataUrl }))}
                    />
                  </>
                )}
              </div>
            )}

            {/* ---- Completion Report-specific fields ---- */}
            {(formData.report_type || '').toLowerCase().includes('completion') && (
              <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50/50 dark:bg-amber-950/20 p-4 space-y-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-amber-900 dark:text-amber-200">
                  <CheckCircle size={16} /> Job Completion Report Details
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Project Reference</Label>
                    <Input value={formData.project_reference} onChange={e => setFormData(p => ({...p, project_reference: e.target.value}))}
                      placeholder="PRJ-2026-001 / Marina Tower Phase 2" data-testid="completion-project-ref" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Project Location</Label>
                    <Input value={formData.project_location} onChange={e => setFormData(p => ({...p, project_location: e.target.value}))}
                      placeholder="Dubai Marina, Tower B, 14th Floor" data-testid="completion-project-loc" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Client Name</Label>
                    <Input value={formData.client_name} onChange={e => setFormData(p => ({...p, client_name: e.target.value}))}
                      placeholder="Mr. Ahmed Khan, Project Manager" data-testid="completion-client" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">LPO / Agreement Reference</Label>
                    <Input value={formData.lpo_reference} onChange={e => setFormData(p => ({...p, lpo_reference: e.target.value}))}
                      placeholder="LPO #12345 dated 2026-02-10" data-testid="completion-lpo" />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs">Work Details</Label>
                  <Textarea value={formData.work_details} onChange={e => {
                    setFormData(p => ({...p, work_details: e.target.value}));
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 360) + 'px';
                  }} rows={5} placeholder="List the scope of works completed — multiple lines auto-expand as you type." className="resize-none overflow-hidden min-h-[110px]" data-testid="completion-work-details" />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs">Confirmation Statement</Label>
                  <Textarea value={formData.confirmation_note} onChange={e => setFormData(p => ({...p, confirmation_note: e.target.value}))}
                    rows={2} className="text-sm italic" data-testid="completion-confirmation" />
                  <p className="text-[10px] text-muted-foreground">Default text auto-fills. Edit if you need a custom confirmation.</p>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs">Additional Notes / Special Instructions</Label>
                  <Textarea value={formData.additional_notes} onChange={e => {
                    setFormData(p => ({...p, additional_notes: e.target.value}));
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 280) + 'px';
                  }} rows={3} placeholder="Anything else worth mentioning (warranties, follow-ups, defects)…" className="resize-none overflow-hidden min-h-[80px]" data-testid="completion-additional-notes" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 border-t border-amber-200 dark:border-amber-900/40">
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label className="text-xs">Client Signatory Name</Label>
                    <Input value={formData.client_signature_name} onChange={e => setFormData(p => ({...p, client_signature_name: e.target.value}))}
                      placeholder="Mr. Ahmed Khan" data-testid="completion-sig-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Sign Date</Label>
                    <Input type="date" value={formData.client_signature_date} onChange={e => setFormData(p => ({...p, client_signature_date: e.target.value}))} data-testid="completion-sig-date" />
                  </div>
                </div>

                <SignaturePadField
                  value={formData.client_signature_data}
                  onChange={(dataUrl) => setFormData(p => ({ ...p, client_signature_data: dataUrl }))}
                />

                <p className="text-[10px] text-muted-foreground">
                  💡 Tip: After creating the report, click <strong>Attach</strong> from the report card to add photos / PDF evidence — images are embedded inline in the printable PDF, and files appear in an "Attached Files" section.
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Reference</Label>
                <Input value={formData.reference} onChange={e => setFormData(p => ({...p, reference: e.target.value}))}
                  placeholder="Ref no., project code..." data-testid="report-reference" />
              </div>
              <div className="space-y-2">
                <Label>Addressed To</Label>
                <Input value={formData.addressed_to} onChange={e => setFormData(p => ({...p, addressed_to: e.target.value}))}
                  placeholder="Recipient name or company" data-testid="report-addressed-to" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Location</Label>
                <Input value={formData.location} onChange={e => setFormData(p => ({...p, location: e.target.value}))}
                  placeholder="Site, building, area..." data-testid="report-location" />
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <select value={formData.priority} onChange={e => setFormData(p => ({...p, priority: e.target.value}))}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="report-priority">
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>PDF Layout</Label>
                <div className="flex items-center gap-2">
                  <select value={formData.layout_template || ''} onChange={e => setFormData(p => ({...p, layout_template: e.target.value}))}
                    className="h-9 flex-1 rounded-md border border-input bg-background px-3 text-sm" data-testid="report-layout-template">
                    <option value="">Use company default</option>
                    <option value="classic">Classic</option>
                    <option value="modern_band">Modern Band</option>
                    <option value="minimalist">Minimalist</option>
                    <option value="compact">Compact</option>
                  </select>
                  <LayoutPreviewButton layout={formData.layout_template || 'classic'} />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>
                Details {(() => {
                  const t = (formData.report_type || '').toLowerCase();
                  const isRichType = t.includes('completion') || t.includes('inspection') || t.includes('daily') || t.includes('weekly') || t.includes('monthly') || t.includes('rfi') || t.includes('incident') || t.includes('progress') || t.includes('delivery');
                  return !isRichType && <span className="text-red-500">*</span>;
                })()}
              </Label>
              <Textarea value={formData.details} onChange={e => setFormData(p => ({...p, details: e.target.value}))}
                rows={5}
                placeholder={(() => {
                  const t = (formData.report_type || '').toLowerCase();
                  const isRichType = t.includes('completion') || t.includes('inspection') || t.includes('daily') || t.includes('weekly') || t.includes('monthly') || t.includes('rfi') || t.includes('incident') || t.includes('progress') || t.includes('delivery');
                  return isRichType ? "Optional supplemental notes (most details are captured in the panel above)." : "Full report details, findings, observations...";
                })()}
                className="resize-y"
                required={(() => {
                  const t = (formData.report_type || '').toLowerCase();
                  const isRichType = t.includes('completion') || t.includes('inspection') || t.includes('daily') || t.includes('weekly') || t.includes('monthly') || t.includes('rfi') || t.includes('incident') || t.includes('progress') || t.includes('delivery');
                  return !isRichType;
                })()}
                data-testid="report-details" />
            </div>

            <div className="space-y-2">
              <Label>Remarks</Label>
              <Textarea value={formData.remarks} onChange={e => setFormData(p => ({...p, remarks: e.target.value}))}
                rows={3} placeholder="Additional remarks, recommendations..." className="resize-y" data-testid="report-remarks" />
            </div>

            {/* ---- Attachments (staged for upload after save) ---- */}
            {!editingId && (
              <div className="space-y-2 rounded-lg border border-dashed border-blue-300 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-950/20 p-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2 text-sm font-semibold text-blue-900 dark:text-blue-200">
                    <Paperclip size={14} /> Attach Photos / PDF Evidence
                  </Label>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      multiple
                      accept="image/*,application/pdf"
                      className="hidden"
                      onChange={e => {
                        const files = Array.from(e.target.files || []);
                        setPendingFiles(prev => [...prev, ...files]);
                        e.target.value = '';
                      }}
                      data-testid="report-attach-input"
                    />
                    <span className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700">
                      <Paperclip size={12} /> Add Files
                    </span>
                  </label>
                </div>
                <p className="text-xs text-blue-800 dark:text-blue-300">
                  Images embed inline in the printable PDF · PDFs merge page-by-page · attachments survive container redeploys (stored in GridFS).
                </p>
                {pendingFiles.length > 0 ? (
                  <ul className="space-y-1" data-testid="report-attach-list">
                    {pendingFiles.map((f, i) => (
                      <li key={i} className="flex items-center justify-between gap-2 rounded-md border border-blue-200 dark:border-blue-900/50 bg-white dark:bg-background px-2 py-1 text-xs">
                        <span className="flex items-center gap-2 truncate">
                          <Paperclip size={11} className="shrink-0 text-blue-600" />
                          <span className="truncate">{f.name}</span>
                          <span className="text-muted-foreground shrink-0">({(f.size / 1024).toFixed(0)} KB)</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => setPendingFiles(prev => prev.filter((_, idx) => idx !== i))}
                          className="text-red-500 hover:text-red-700"
                          aria-label="Remove file"
                        >
                          <X size={12} />
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-muted-foreground italic">No files added yet — click <strong>Add Files</strong> above.</p>
                )}
              </div>
            )}

            {/* ---- Attachments (edit mode — direct upload to existing report) ---- */}
            {editingId && (
              <div className="space-y-2 rounded-lg border border-dashed border-blue-300 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-950/20 p-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2 text-sm font-semibold text-blue-900 dark:text-blue-200">
                    <Paperclip size={14} /> Attachments ({formData.attachments?.length || 0})
                  </Label>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      multiple
                      accept="image/*,application/pdf"
                      className="hidden"
                      onChange={async (e) => {
                        const files = Array.from(e.target.files || []);
                        for (const f of files) {
                          try {
                            const fd = new FormData();
                            fd.append('file', f);
                            const res = await fetch(`${API}/api/reports/${editingId}/attachments`, {
                              method: 'POST', headers: authHeaders(), body: fd,
                            });
                            if (!res.ok) throw new Error('upload failed');
                          } catch { toast.error(`Failed to upload ${f.name}`); }
                        }
                        // refresh attachments list
                        try {
                          const r = await fetch(`${API}/api/reports/${editingId}`, { headers: authHeaders() });
                          if (r.ok) {
                            const data = await r.json();
                            setFormData(p => ({ ...p, attachments: data.attachments || [] }));
                          }
                        } catch { /* soft-fail: keep the modal open */ }
                        toast.success(`${files.length} file(s) uploaded`);
                        e.target.value = '';
                      }}
                      data-testid="report-attach-input-edit"
                    />
                    <span className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700">
                      <Paperclip size={12} /> Add Files
                    </span>
                  </label>
                </div>
                <p className="text-xs text-blue-800 dark:text-blue-300">
                  Perfect for photos, site notices, incident reports, marked-up drawings, etc. Images embed inline in the printable PDF.
                </p>
                {(formData.attachments && formData.attachments.length > 0) ? (
                  <ul className="space-y-1">
                    {formData.attachments.map((att) => (
                      <li key={att.id} className="flex items-center justify-between gap-2 rounded-md border border-blue-200 dark:border-blue-900/50 bg-white dark:bg-background px-2 py-1 text-xs">
                        <span className="flex items-center gap-2 truncate">
                          <Paperclip size={11} className="shrink-0 text-blue-600" />
                          <span className="truncate">{att.filename || att.original_filename}</span>
                          <span className="text-muted-foreground shrink-0">({((att.size || att.file_size || 0) / 1024).toFixed(0)} KB)</span>
                        </span>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!window.confirm('Remove this attachment?')) return;
                            try {
                              await fetch(`${API}/api/reports/${editingId}/attachments/${att.id}`, { method: 'DELETE', headers: authHeaders() });
                              toast.success('Attachment removed');
                              setFormData(p => ({ ...p, attachments: (p.attachments || []).filter(a => a.id !== att.id) }));
                            } catch { toast.error('Delete failed'); }
                          }}
                          className="text-red-500 hover:text-red-700"
                        >✕</button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-muted-foreground italic">No attachments yet — click <strong>Add Files</strong> above.</p>
                )}
              </div>
            )}

            <DialogFooter className="flex-shrink-0 pt-2">
              {!editingId && <div className="mr-auto"><DraftSavedIndicator savedAt={reportDraft.lastAutoSavedAt} /></div>}
              <Button type="submit" disabled={formLoading} data-testid="submit-report-btn">
                {formLoading ? <CircleNotch size={16} className="animate-spin mr-2" /> : null}
                {editingId ? 'Update Report' : 'Create Report'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Custom Type Dialog */}
      <Dialog open={showAddType} onOpenChange={setShowAddType}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Add Custom Report Type</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={newTypeName} onChange={e => setNewTypeName(e.target.value)}
              placeholder="e.g. Material Inspection Report" data-testid="new-type-input" />
            <Button onClick={handleAddCustomType} className="w-full" data-testid="save-type-btn">Add Type</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Report Dialog */}
      {showView && (
        <Dialog open={!!showView} onOpenChange={(o) => { if (!o) setShowView(null); }}>
          <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <DialogTitle className="flex items-center gap-3">
                    {showView.report_number}
                    <Badge variant="outline" className={STATUS_COLORS[showView.status]}>{showView.status}</Badge>
                    <Badge variant="outline" className={PRIORITY_COLORS[showView.priority]}>{showView.priority}</Badge>
                  </DialogTitle>
                  <p className="text-sm text-muted-foreground mt-1">{showView.report_type}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleViewPdf(showView)} data-testid="view-pdf-inline">
                    <FilePdf size={15} className="mr-1" /> View PDF
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDownloadPdf(showView)} data-testid="view-download-pdf">
                    <Download size={15} className="mr-1" /> Download
                  </Button>
                  {showView.status === 'draft' && (
                    <Button size="sm" onClick={() => handleFinalize(showView.id)} data-testid="view-finalize-btn">
                      <CheckCircle size={15} className="mr-1" /> Finalize
                    </Button>
                  )}
                </div>
              </div>
            </DialogHeader>
            <div className="overflow-y-auto flex-1 space-y-4 pr-1">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                <div><p className="text-xs text-muted-foreground uppercase font-semibold">Date</p><p className="font-medium">{showView.date}</p></div>
                {showView.reference && <div><p className="text-xs text-muted-foreground uppercase font-semibold">Reference</p><p className="font-medium">{showView.reference}</p></div>}
                <div><p className="text-xs text-muted-foreground uppercase font-semibold">Prepared By</p><p className="font-medium">{showView.prepared_by}</p></div>
                {showView.addressed_to && <div><p className="text-xs text-muted-foreground uppercase font-semibold">Addressed To</p><p className="font-medium">{showView.addressed_to}</p></div>}
                {showView.location && <div><p className="text-xs text-muted-foreground uppercase font-semibold">Location</p><p className="font-medium">{showView.location}</p></div>}
                {showView.finalized_by && <div><p className="text-xs text-muted-foreground uppercase font-semibold">Finalized By</p><p className="font-medium">{showView.finalized_by}</p></div>}
              </div>

              {showView.subject && (
                <div className="bg-muted/30 rounded-lg p-4">
                  <p className="text-xs text-muted-foreground uppercase font-semibold mb-1">Subject</p>
                  <p className="font-medium">{showView.subject}</p>
                </div>
              )}

              <div className="border rounded-lg overflow-hidden">
                <div className="bg-[#1e3a5f] text-white px-4 py-2 text-sm font-semibold uppercase">Details</div>
                <div className="p-4 text-sm whitespace-pre-wrap leading-relaxed">{showView.details || 'No details'}</div>
              </div>

              {showView.remarks && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-[#1e3a5f] text-white px-4 py-2 text-sm font-semibold uppercase">Remarks</div>
                  <div className="p-4 text-sm whitespace-pre-wrap leading-relaxed">{showView.remarks}</div>
                </div>
              )}

              {/* Attachments */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-muted/50 px-4 py-2 flex items-center justify-between">
                  <span className="text-sm font-semibold flex items-center gap-2"><Paperclip size={15} /> Attachments ({showView.attachments?.length || 0})</span>
                  {showView.status === 'draft' && (
                    <label className="cursor-pointer">
                      <input type="file" className="hidden" onChange={e => { if (e.target.files[0]) handleUploadAttachment(showView.id, e.target.files[0]); e.target.value=''; }} />
                      <span className="text-xs text-primary hover:underline flex items-center gap-1"><Plus size={12} /> Upload</span>
                    </label>
                  )}
                </div>
                <div className="p-3">
                  {(!showView.attachments || showView.attachments.length === 0) ? (
                    <p className="text-xs text-muted-foreground text-center py-2">No attachments</p>
                  ) : (
                    <div className="space-y-2">
                      {showView.attachments.map(att => (
                        <div key={att.id} className="flex items-center justify-between bg-muted/30 rounded-md px-3 py-2 text-sm">
                          <div className="flex items-center gap-2 truncate">
                            <FileText size={14} className="text-muted-foreground shrink-0" />
                            <span className="truncate">{att.filename}</span>
                            <span className="text-xs text-muted-foreground">({(att.size / 1024).toFixed(1)} KB)</span>
                          </div>
                          {showView.status === 'draft' && (
                            <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => handleDeleteAttachment(showView.id, att.id)}>
                              <X size={12} />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}


/* ----------------------------------------------------------------------- */
/*  SignaturePadField — minimal pointer-events signature capture.          */
/*  Exports a base64 PNG via onChange whenever the user lifts the pointer. */
/* ----------------------------------------------------------------------- */

// ==================== Critical Tasks Panel ====================
function CriticalTasksPanel({ value, onChange, reportId }) {
  const tasks = Array.isArray(value) ? value : [];
  const [templates, setTemplates] = useState({ builtin: [], custom: [] });
  const [templatesLoaded, setTemplatesLoaded] = useState(false);
  const [saveTplName, setSaveTplName] = useState('');

  const loadTemplates = async () => {
    if (templatesLoaded) return;
    try {
      const res = await fetch(`${API}/api/reports/task-templates`, { headers: authHeaders() });
      if (!res.ok) throw new Error('load failed');
      const data = await res.json();
      setTemplates(data || { builtin: [], custom: [] });
      setTemplatesLoaded(true);
    } catch { /* silent */ }
  };

  const applyTemplate = (tplTasks) => {
    // Reset percents to 0 when applying a fresh template
    const fresh = (tplTasks || []).map(t => ({
      name: t.name || 'Task',
      percent_complete: 0,
      weight: t.weight || 1,
      notes: '',
      action_by: '',
      status: '',
      sub_activities: (t.sub_activities || []).map(s => ({ name: s.name, percent_complete: 0 })),
    }));
    onChange(fresh);
    toast.success(`${fresh.length} tasks loaded`);
  };

  const copyFromPrevious = async () => {
    if (!reportId) { toast.info('Save the report once, then copy tasks from a previous report.'); return; }
    try {
      const res = await fetch(`${API}/api/reports/${reportId}/previous-tasks`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'failed');
      const prev = data?.critical_tasks || [];
      if (!prev.length) { toast.info('No previous progress report found for this project.'); return; }
      onChange(prev);
      toast.success(`Copied ${prev.length} tasks from ${data.source_report_number || 'previous report'}`);
    } catch (e) {
      toast.error(e.message || 'Failed to copy from previous report');
    }
  };

  const saveAsTemplate = async () => {
    const name = (saveTplName || '').trim();
    if (!name) { toast.error('Enter a template name'); return; }
    if (!tasks.length) { toast.error('Add at least one task first'); return; }
    try {
      const res = await fetch(`${API}/api/reports/task-templates`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, tasks }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'save failed');
      toast.success(`Template "${name}" saved`);
      setSaveTplName('');
      setTemplatesLoaded(false);
      await loadTemplates();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    }
  };

  const addTask = () => onChange([...tasks, { name: '', percent_complete: 0, weight: 1, notes: '', action_by: '', expected_completion_date: '', sub_activities: [] }]);
  // Delete with confirm + undo (Feb 2026 — user asked to prevent accidental
  // loss and be able to restore up to the last 5 removed tasks).
  const [deletedStack, setDeletedStack] = useState([]);   // [{task, index, ts}]
  const removeTask = (i) => {
    const t = tasks[i];
    const label = (t?.name || '').trim() || `Task #${i + 1}`;
    if (!window.confirm(`Delete "${label}"?\n\nYou can Undo the last deletion right after — but only until the next Save.`)) return;
    const removed = tasks[i];
    onChange(tasks.filter((_, idx) => idx !== i));
    setDeletedStack((s) => [{ task: removed, index: i, ts: Date.now() }, ...s].slice(0, 5));
    toast.success(`Removed "${label}"`, {
      action: {
        label: 'Undo',
        onClick: () => {
          // Re-insert the removed task at its original position (best-effort).
          onChange((cur => {
            const next = [...cur];
            const at = Math.max(0, Math.min(i, next.length));
            next.splice(at, 0, removed);
            return next;
          })(tasks.filter((_, idx) => idx !== i)));
          setDeletedStack((s) => s.slice(1));
          toast.info(`Restored "${label}"`);
        },
      },
      duration: 8000,
    });
  };
  const restoreLastDeleted = () => {
    if (!deletedStack.length) return;
    const [head, ...rest] = deletedStack;
    const at = Math.max(0, Math.min(head.index, tasks.length));
    const next = [...tasks];
    next.splice(at, 0, head.task);
    onChange(next);
    setDeletedStack(rest);
    toast.info(`Restored "${(head.task.name || '').trim() || 'task'}"`);
  };
  const setTask = (i, patch) => onChange(tasks.map((t, idx) => idx === i ? { ...t, ...patch } : t));
  const addSub = (i) => setTask(i, { sub_activities: [...(tasks[i].sub_activities || []), { name: '', percent_complete: 0 }] });
  const removeSub = (i, j) => {
    const s = (tasks[i].sub_activities || [])[j];
    const label = (s?.name || '').trim() || `Sub-activity #${j + 1}`;
    if (!window.confirm(`Delete "${label}"?`)) return;
    setTask(i, { sub_activities: (tasks[i].sub_activities || []).filter((_, idx) => idx !== j) });
  };
  const setSub = (i, j, patch) => setTask(i, {
    sub_activities: (tasks[i].sub_activities || []).map((s, idx) => idx === j ? { ...s, ...patch } : s),
  });

  // Weighted average preview + total weight (Feb 2026 — user wants to
  // see the sum-of-weights so they can verify it tallies to 100 %).
  const overall = (() => {
    let w = 0, s = 0;
    tasks.forEach(t => {
      const tw = Number(t.weight) > 0 ? Number(t.weight) : 1;
      const tp = Math.max(0, Math.min(100, Number(t.percent_complete) || 0));
      w += tw; s += tp * tw;
    });
    return w > 0 ? (s / w) : 0;
  })();
  const totalWeight = tasks.reduce((a, t) => a + (Number(t.weight) > 0 ? Number(t.weight) : 1), 0);
  const weightOk = tasks.length === 0 || Math.abs(totalWeight - 100) < 0.5;

  const statusOf = (t) => {
    if ((t.status || '').toLowerCase() === 'blocked') return 'blocked';
    const p = Number(t.percent_complete) || 0;
    if (p >= 100) return 'completed';
    if (p > 0) return 'in_progress';
    return 'not_started';
  };
  const STATUS_UI = {
    not_started: { color: 'bg-slate-200 text-slate-700', label: 'Not Started' },
    in_progress: { color: 'bg-amber-100 text-amber-800', label: 'In Progress' },
    blocked:     { color: 'bg-rose-100 text-rose-800',   label: 'Blocked' },
    completed:   { color: 'bg-emerald-100 text-emerald-800', label: 'Completed' },
  };

  return (
    <div className="rounded-lg border border-blue-200 dark:border-blue-900/40 bg-blue-50/40 dark:bg-blue-950/20 p-3 space-y-3" data-testid="rt-critical-tasks-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <Label className="text-sm font-semibold">Critical Tasks — Project Dashboard</Label>
          <div className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">
            <span>Weighted overall: <b>{overall.toFixed(1)}%</b></span>
            <span>·</span>
            <span>{tasks.length} tasks</span>
            <span>·</span>
            <span className={weightOk ? 'text-emerald-600 font-semibold' : 'text-amber-600 font-semibold'} title="Weights should tally to 100">
              Total weight: {totalWeight.toFixed(1)}
              {weightOk ? ' ✓' : ` — adjust to 100 (off by ${(100 - totalWeight).toFixed(1)})`}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {deletedStack.length > 0 && (
            <Button type="button" size="sm" variant="outline" onClick={restoreLastDeleted} className="border-amber-400 text-amber-700" data-testid="rt-undo-delete">
              ↺ Undo delete ({deletedStack.length})
            </Button>
          )}
          <select
            className="h-8 px-2 text-xs rounded border border-input bg-background"
            onFocus={loadTemplates}
            onChange={(e) => {
              const val = e.target.value;
              e.target.value = '';
              if (!val) return;
              const [type, id] = val.split(':');
              const src = (type === 'b' ? templates.builtin : templates.custom).find(t => t.id === id);
              if (src) applyTemplate(src.tasks);
            }}
            data-testid="rt-tasks-template-select"
          >
            <option value="">Load Template…</option>
            <optgroup label="Built-in">
              {templates.builtin.map(t => <option key={t.id} value={`b:${t.id}`}>{t.name}</option>)}
            </optgroup>
            {templates.custom.length > 0 && (
              <optgroup label="Company Templates">
                {templates.custom.map(t => <option key={t.id} value={`c:${t.id}`}>{t.name}</option>)}
              </optgroup>
            )}
          </select>
          <Button type="button" size="sm" variant="outline" onClick={copyFromPrevious} data-testid="rt-copy-prev-tasks">Copy from Previous</Button>
          <Button type="button" size="sm" onClick={addTask} data-testid="rt-add-task">+ Add Task</Button>
        </div>
      </div>

      {tasks.length === 0 && (
        <div className="text-xs italic text-muted-foreground py-4 text-center border border-dashed border-blue-200 rounded">
          No tasks yet. Click <b>+ Add Task</b>, load a template, or copy from a previous report.
        </div>
      )}

      {tasks.map((t, i) => {
        const st = statusOf(t);
        const ui = STATUS_UI[st];
        return (
          <div key={i} className="rounded border border-border/60 bg-background p-2 space-y-2" data-testid={`rt-task-${i}`}>
            <div className="grid grid-cols-12 gap-1 items-center">
              <span className="col-span-1 text-xs text-muted-foreground text-center">{i + 1}.</span>
              <Input className="col-span-4 h-8 text-xs" placeholder="Task name (e.g. Material Delivery)" value={t.name || ''} onChange={(e) => setTask(i, { name: e.target.value })} data-testid={`rt-task-name-${i}`} />
              <Input className="col-span-1 h-8 text-xs text-right" type="number" min={0} max={100} step={1} placeholder="%" value={t.percent_complete ?? ''} onChange={(e) => setTask(i, { percent_complete: e.target.value === '' ? 0 : Number(e.target.value) })} data-testid={`rt-task-pct-${i}`} title="How much of this task is done, 0–100" />
              <Input className="col-span-1 h-8 text-xs text-right" type="number" min={0.1} step={0.1} placeholder="Wt" value={t.weight ?? 1} onChange={(e) => setTask(i, { weight: e.target.value === '' ? 1 : Number(e.target.value) })} title="Weight — how important this task is vs others. All weights should tally to 100." />
              <select className={`col-span-2 h-8 text-xs rounded border border-input px-2 ${ui.color}`} value={t.status || ''} onChange={(e) => setTask(i, { status: e.target.value })} data-testid={`rt-task-status-${i}`} title="Auto derives status from %. Pick Blocked to flag manually.">
                <option value="">Auto ({ui.label})</option>
                <option value="blocked">Blocked</option>
              </select>
              <Input className="col-span-2 h-8 text-xs" placeholder="Action By" value={t.action_by || ''} onChange={(e) => setTask(i, { action_by: e.target.value })} data-testid={`rt-task-action-${i}`} title="Who is responsible / who we are waiting on for this task." />
              <Button type="button" size="icon" variant="ghost" className="col-span-1 h-7 w-7 text-red-500 hover:bg-red-50" onClick={() => removeTask(i)} title="Delete this task (with confirmation + undo)" data-testid={`rt-task-remove-${i}`}>✕</Button>
            </div>
            {/* Field hint strip — legend for the row above (col grid mirrored). */}
            <div className="grid grid-cols-12 gap-1 -mt-1 px-1 text-[10px] text-muted-foreground leading-tight">
              <span className="col-span-1"></span>
              <span className="col-span-4">Short activity name shown in the PDF.</span>
              <span className="col-span-1 text-right">% done</span>
              <span className="col-span-1 text-right">Weight</span>
              <span className="col-span-2 text-center">Status</span>
              <span className="col-span-2">Action owner (or waiting on)</span>
              <span className="col-span-1 text-center">Delete</span>
            </div>
            <div className="grid grid-cols-12 gap-1">
              <span className="col-span-1"></span>
              <Textarea className="col-span-8 h-14 text-xs resize-none" placeholder="Notes / pending approvals (visible in Action Required callout — only shown for non-completed tasks)" value={t.notes || ''} onChange={(e) => setTask(i, { notes: e.target.value })} data-testid={`rt-task-notes-${i}`} />
              <div className="col-span-3 space-y-1">
                <Input
                  type="date"
                  className="h-8 text-xs w-full"
                  value={t.expected_completion_date || ''}
                  onChange={(e) => setTask(i, { expected_completion_date: e.target.value })}
                  data-testid={`rt-task-eta-${i}`}
                  title="Expected date this task will be completed. Only shown on the PDF for pending / in-progress / blocked tasks."
                />
                <div className="text-[10px] text-muted-foreground leading-tight">
                  Expected completion date (only appears in the PDF Pending Works section if this task isn&apos;t already 100 %).
                </div>
              </div>
            </div>
            {/* Sub-activities */}
            <div className="pl-6 space-y-1">
              {(t.sub_activities || []).map((s, j) => (
                <div key={j} className="grid grid-cols-12 gap-1 items-center">
                  <span className="col-span-1 text-[11px] text-muted-foreground text-right">↳</span>
                  <Input className="col-span-8 h-7 text-xs" placeholder="Sub-activity name" value={s.name || ''} onChange={(e) => setSub(i, j, { name: e.target.value })} />
                  <Input className="col-span-2 h-7 text-xs text-right" type="number" min={0} max={100} step={1} placeholder="%" value={s.percent_complete ?? ''} onChange={(e) => setSub(i, j, { percent_complete: e.target.value === '' ? 0 : Number(e.target.value) })} />
                  <Button type="button" size="icon" variant="ghost" className="col-span-1 h-6 w-6" onClick={() => removeSub(i, j)}>✕</Button>
                </div>
              ))}
              <Button type="button" size="sm" variant="ghost" className="text-[11px] h-6" onClick={() => addSub(i)}>+ Sub-activity</Button>
            </div>
          </div>
        );
      })}

      {tasks.length > 0 && (
        <div className="flex items-center gap-2 pt-2 border-t border-blue-200/60">
          <Input placeholder="Template name to save…" value={saveTplName} onChange={(e) => setSaveTplName(e.target.value)} className="h-8 text-xs flex-1" />
          <Button type="button" size="sm" variant="outline" onClick={saveAsTemplate}>Save as Template</Button>
        </div>
      )}
    </div>
  );
}


function SignaturePadField({ value, onChange }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);

  // Repaint existing signature (e.g. when editing an existing report)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!value) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    };
    img.src = value;
  }, [value]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#111827';
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const end = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    drawingRef.current = false;
    const canvas = canvasRef.current;
    onChange(canvas.toDataURL('image/png'));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onChange('');
  };

  return (
    <div className="space-y-1.5" data-testid="signature-pad-wrap">
      <div className="flex items-center justify-between">
        <Label className="text-xs">Client Signature (sign with mouse / finger)</Label>
        <button type="button" onClick={clear} className="text-[10px] text-rose-600 hover:underline" data-testid="signature-clear">
          Clear
        </button>
      </div>
      <div className="rounded-md border-2 border-dashed border-amber-300 dark:border-amber-800 bg-white">
        <canvas
          ref={canvasRef}
          width={600}
          height={160}
          className="w-full h-40 cursor-crosshair touch-none"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          data-testid="signature-canvas"
        />
      </div>
      {value ? (
        <p className="text-[10px] text-emerald-700 dark:text-emerald-400">✓ Signature captured</p>
      ) : (
        <p className="text-[10px] text-muted-foreground">Optional — sign in the box above. Saved as PNG inside the PDF.</p>
      )}
    </div>
  );
}

